import { createSlice } from '@reduxjs/toolkit';
import { BASE_URL } from '@utils/constants.js';

const initialState = {
  isLoading: false,
  isActive: false,
  id: 0,
  uid: 0,
  sid: 0,

  layout: 1,
  viewType: 1,

  profilePic: {
    isDisplay: true,
    picUrl: `${BASE_URL}/content/profile_pic.jpg`,
    size: 100,
    type: null,
    videoUrl: null,
    height: 200,
    cutoutUrl: null,
    // Vertical variant of the cutout (No background / Text behind when Photo shape = Vertical);
    // null → falls back to the square cutout.
    cutoutVerticalUrl: null,
    // Decorative cutout effect: 'none' | 'outline' | 'stacked' + color + stacked direction.
    cutoutEffect: 'none',
    cutoutEffectColor: '#FF53A5',
    cutoutEffectDirection: 'left',
    // Separate vertical crop for layout 6 (vertical photo). null → layout 6 falls back to
    // the original-proportion gl_ of picUrl. Independent from picUrl (square, layouts 1-5).
    verticalPicUrl: null,
    // Layout 4/5 photo shape: true → use the vertical crop instead of the square photo.
    useVertical: false,
    // Photo style: 'original' (photo as-is) | 'cutout' (background removed) |
    // 'layered' (photo → text → cutout, "text behind subject" depth effect).
    avatarMode: 'original',
    // Layered-mode overlay text. Empty string → the profile name is used.
    overlayText: '',
    overlayColor: '#ffffff',
    overlaySize: 44,
    overlayY: 50,
    // Animated treatment for the overlay text: none | shimmer | glow | gradient | sparkle
    overlayEffect: 'none',
    // Layout 5 slider display: 'swipe' (one photo, swipe) | 'gallery' (main + thumbnail row).
    sliderStyle: 'swipe',
    // Layout 5 cover vertical framing: 0 (top) … 50 (center) … 100 (bottom). Maps to
    // object-position on the cover photo so a portrait shot isn't cropped at the head.
    coverPosition: 50,
    // Layout 7 "Photo hero": second photo used as the fixed full-viewport backdrop. Empty →
    // falls back to picUrl (compact avatar doubles as the backdrop).
    backdropUrl: '',
  },
  cover: {
    image: '',
    cta: [],
    darkness: 0,
    description: '',
    height: 276,
    isText: false,
    textColor: '#000000',
    textPosition: 0,
    title: '',
    type: 1,
    video: '',
  },
  fullName: {
    bold: 'normal',
    font: '',
    fullName: '',
    image: '',
    size: 15,
    stroke: 0,
    strokeColor: '#000000',
    type: null,
    position: 100,
  },
  social: {
    backgroundColor: '#ffffff',
    border: 'none',
    borderColor: '#ECECF0',
    color: '#000000',
    iconAlign: 'top',
    iconStyle: 'thin',
    isActive: false,
    isBold: false,
    isCustom: false,
    items: [],
    opacity: 0,
    shadow: 'none',
    shadowColor: '#000000',
    shape: 'none',
    gap: 12,
    size: 24,
  },
  shortBio: '',
  shortBioSize: 13,
  externalUrl: '',
  location: '',
  locationMap: '',
  isVerifiedBadge: false,
  profileBadges: [],
  buttons: [],
  contacts: [],
  thought: '',
  thoughtButtonTitle: '',
  thoughtButtonUrl: '',
  stories: [],
  storyUrl: '',
  subscribeOptions: [],
  isVCard: false,
  sliderItems: [],
  music: {
    url: '',
    startVolume: 1,
    title: '',
    playerStyle: 'minimal',
    autoplay: false,
  },
  profilePicRatio: 'square',
  statuses: [],
  showTotalFollowers: false,
  totalFollowersColor: '',
  totalFollowers: null,
  tags: {
    isActive: true,
    items: [],
  },
  ribbon: null,
  // Featured header widget — one card lifting an existing block's core action into the header
  // (layouts 3-7). Stored as a reference to the source block, resolved at render time:
  // { type, blockType, blockSid, position }. null = no widget.
  widget: null,
  vCard: {
    buttonPosition: 'header',
    buttonIcon: 'user',
    buttonTitle: 'Save contact',
    buttonBackgroundColor: '#000000',
    buttonTextColor: '#ffffff',
    buttonShadow: 'none',
    buttonShadowColor: '#000',
    buttonBorder: 'none',
    buttonBorderColor: '#ECECF0',
    buttonShape: 'round',
    buttonOpacity: 0,

    firstName: '',
    lastName: '',
    jobTitle: '',
    organization: '',
    email: '',
    workPhone: '',
    mobilePhone: '',
    website: '',
    information: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  },

  pictureDesign: {
    shape: 'round',
    border: 'none',
    borderColor: '#ECECF0',
    shadow: 'none',
    shadowColor: '#000000',
    gradient: '#ffffff',
    gradientAuto: false,
    gradientMode: 'custom',
    gradientPosition: 'photo',
  },
  buttonDesign: {
    shape: 'small',
    border: 'none',
    borderColor: '#ECECF0',
    shadow: 'none',
    shadowColor: '#000000',
    direction: 'row',
  },
  containerDesign: {
    textColor: '#000000',
    backgroundColor: '#ffffff',
    opacity: 0,
    isBackground: false,
    backgroundType: 1,
    backgroundStyle: 1,
    backgroundImage: '',
    gradientColor1: '#B3F7EF',
    gradientColor2: '#F7D6E0',
    gradientDirection: 0,
    gradientOffset: 0,
    blur: 0,
    brightness: 100,
    contrast: 100,
    backgroundAnimation: 'none',
    bioHighlight: 0,
    bioHighlightColor: '#ffffff',
    bioStroke: 0,
    nameHighlight: 0,
    nameHighlightColor: '#ffffff',
  },
};

export const headerSlice = createSlice({
  name: 'header',
  initialState,
  reducers: {
    headerStore: (state, action) => {
      const { id, isActive, sid, uid, data } = action.payload;
      const block = data ?? {};
      const { blockContent = {}, blockDesign = {} } = block;
      const {
        layout,
        viewType,
        profilePic = {},
        cover = {},
        fullName = {},
        social = {},
        shortBio,
        shortBioSize,
        externalUrl,
        location,
        locationMap,
        isVerifiedBadge,
        profileBadges,
        buttons,
        contacts,
        thought,
        stories,
        storyUrl,
        subscribeOptions,
        vCard,
      } = blockContent;
      const { pictureDesign = {}, buttonDesign = {}, containerDesign = {} } = blockDesign;

      state.isLoading = false;
      state.id = id ?? 0;
      state.isActive = isActive;
      state.sid = sid;
      state.uid = uid;

      state.layout = layout ?? 1;
      state.viewType = viewType ?? 1;

      state.profilePic = {
        isDisplay: profilePic.isDisplay ?? blockContent.showProfilePic ?? true,
        picUrl: profilePic.picUrl ?? `${BASE_URL}/content/profile_pic.jpg`,
        size: profilePic.size ?? 100,
        type: profilePic.type ?? null,
        videoUrl: profilePic.videoUrl ?? null,
        height: profilePic.height ?? 462,
        cutoutUrl: profilePic.cutoutUrl ?? null,
        cutoutVerticalUrl: profilePic.cutoutVerticalUrl ?? null,
        cutoutEffect: profilePic.cutoutEffect ?? 'none',
        cutoutEffectColor: profilePic.cutoutEffectColor ?? '#FF53A5',
        cutoutEffectDirection: profilePic.cutoutEffectDirection ?? 'left',
        verticalPicUrl: profilePic.verticalPicUrl ?? null,
        useVertical: profilePic.useVertical ?? false,
        // Backward-compat: pre-avatarMode profiles map by cutout presence.
        avatarMode: profilePic.avatarMode ?? (profilePic.cutoutUrl ? 'cutout' : 'original'),
        overlayText: profilePic.overlayText ?? '',
        overlayColor: profilePic.overlayColor ?? '#ffffff',
        overlaySize: profilePic.overlaySize ?? 44,
        overlayY: profilePic.overlayY ?? 50,
        overlayEffect: profilePic.overlayEffect ?? 'none',
        sliderStyle: profilePic.sliderStyle ?? 'swipe',
        coverPosition: profilePic.coverPosition ?? 50,
        backdropUrl: profilePic.backdropUrl ?? '',
      };
      state.cover = {
        image: cover.image ?? '',
        cta: cover.cta ?? [],
        darkness: cover.darkness ?? 0,
        description: cover.description ?? '',
        height: cover.height ?? 276,
        isText: cover.isText ?? false,
        textColor: cover.textColor ?? '#000000',
        textPosition: cover.textPosition ?? 0,
        title: cover.title ?? '',
        type: cover.type ?? 1,
        video: cover.video ?? '',
      };
      state.fullName = {
        bold: fullName.bold ?? 'normal',
        font: fullName.font ?? '',
        fullName: fullName.fullName ?? '',
        image: fullName.image ?? '',
        size: fullName.size ?? 15,
        stroke: fullName.stroke ?? 0,
        strokeColor: fullName.strokeColor ?? '#000000',
        type: fullName.type ?? null,
        position: fullName.position ?? 100,
        textColor: fullName.textColor ?? '#ffffff',
      };
      state.social = {
        backgroundColor: social.backgroundColor ?? '#ffffff',
        border: social.border ?? 'none',
        borderColor: social.borderColor ?? '#ECECF0',
        color: social.color ?? '#000000',
        iconAlign: social.iconAlign ?? 'top',
        iconStyle: social.iconStyle ?? 'thin',
        isActive: social.isActive ?? true,
        isBold: social.isBold ?? false,
        isCustom: social.isCustom ?? false,
        items: social.items ?? [],
        opacity: social.opacity ?? 0,
        shadow: social.shadow ?? 'none',
        shadowColor: social.shadowColor ?? '#000000',
        shape: social.shape ?? 'none',
        gap: social.gap ?? 12,
        size: social.size ?? 24,
      };
      state.shortBio = shortBio ?? '';
      state.shortBioSize = shortBioSize ?? 13;
      state.externalUrl = externalUrl ?? '';
      state.location = location ?? '';
      state.locationMap = locationMap ?? '';
      state.isVerifiedBadge = isVerifiedBadge ?? false;
      state.profileBadges = Array.isArray(profileBadges) ? profileBadges : [];
      state.buttons = buttons ?? [];
      state.contacts = contacts ?? [];
      state.thought = thought ?? '';
      state.thoughtButtonTitle = blockContent.thoughtButtonTitle ?? '';
      state.thoughtButtonUrl = blockContent.thoughtButtonUrl ?? '';
      state.stories = stories ?? [];
      state.storyUrl = storyUrl ?? '';
      state.sliderItems = blockContent.sliderItems ?? [];
      state.music = blockContent.music?.url ? blockContent.music : state.music;
      state.profilePicRatio = blockContent?.profilePicRatio ?? 'square';
      state.statuses = (blockContent.statuses ?? []).map((s) => ({ ...s, id: s.id || crypto.randomUUID() }));
      state.showTotalFollowers = blockContent.showTotalFollowers ?? false;
      state.totalFollowersColor = blockContent.totalFollowersColor ?? '';
      state.totalFollowers = blockContent.totalFollowers ?? null;
      state.tags = {
        isActive: blockContent.tags?.isActive ?? true,
        items: Array.isArray(blockContent.tags?.items) ? blockContent.tags.items : [],
      };
      state.ribbon = blockContent.ribbon ?? null;
      state.widget = blockContent.widget ?? null;
      state.subscribeOptions = subscribeOptions ?? [];
      state.isVCard = Boolean(vCard);
      state.vCard = {
        ...vCard,
        buttonPosition: vCard?.buttonPosition ?? 'header',
        buttonIcon: vCard?.buttonIcon ?? 'user',
        buttonTitle: vCard?.buttonTitle ?? 'Save contact',
        buttonBackgroundColor: vCard?.buttonBackgroundColor ?? '#000000',
        buttonTextColor: vCard?.buttonTextColor ?? '#ffffff',
        buttonOpacity: vCard?.buttonOpacity ?? 0,
        buttonBorder: vCard?.buttonBorder ?? 'none',
        buttonBorderColor: vCard?.buttonBorderColor ?? '#000',
        buttonShadow: vCard?.buttonShadow ?? 'none',
        buttonShadowColor: vCard?.buttonShadowColor ?? '#000',
        buttonShape: vCard?.buttonShape ?? 'round',

        firstName: vCard?.firstName ?? '',
        lastName: vCard?.lastName ?? '',
        jobTitle: vCard?.jobTitle ?? '',
        organization: vCard?.organization ?? '',
        email: vCard?.email ?? '',
        workPhone: vCard?.workPhone ?? '',
        mobilePhone: vCard?.mobilePhone ?? '',
        website: vCard?.website ?? '',
        information: vCard?.information ?? '',
        streetAddress: vCard?.streetAddress ?? '',
        city: vCard?.city ?? '',
        state: vCard?.state ?? '',
        postalCode: vCard?.postalCode ?? '',
        country: vCard?.country ?? '',
      };

      state.buttonDesign = {
        shape: buttonDesign.shape ?? 'small',
        border: buttonDesign.border ?? 'none',
        borderColor: buttonDesign.borderColor ?? '#ECECF0',
        shadow: buttonDesign.shadow ?? 'none',
        shadowColor: buttonDesign.shadowColor ?? '#000000',
        direction: buttonDesign.direction ?? 'row',
      };
      state.pictureDesign = {
        shape: pictureDesign.shape ?? 'round',
        border: pictureDesign.border ?? 'none',
        borderColor: pictureDesign.borderColor ?? '#ECECF0',
        shadow: pictureDesign.shadow ?? 'none',
        shadowColor: pictureDesign.shadowColor ?? '#000000',
        gradient: pictureDesign.gradient ?? '#ffffff',
        gradientAuto: pictureDesign.gradientAuto ?? false,
        gradientMode: pictureDesign.gradientMode ?? (pictureDesign.gradientAuto ? 'auto' : 'custom'),
        gradientPosition: pictureDesign.gradientPosition ?? 'photo',
      };
      console.log('[headerSlice] headerSetData incoming containerDesign.infoDesign:', containerDesign.infoDesign);
      state.containerDesign = {
        backgroundColor: containerDesign.backgroundColor ?? '#ffffff',
        textColor: containerDesign.textColor ?? '#000000',
        opacity: containerDesign.opacity ?? 0,
        isBackground: containerDesign.isBackground ?? false,
        backgroundType: containerDesign.backgroundType ?? 1,
        backgroundStyle: containerDesign.backgroundStyle ?? 1,
        backgroundImage: containerDesign.backgroundImage ?? '',
        gradientColor1: containerDesign.gradientColor1 ?? '#B3F7EF',
        gradientColor2: containerDesign.gradientColor2 ?? '#F7D6E0',
        gradientDirection: containerDesign.gradientDirection ?? 0,
        gradientOffset: containerDesign.gradientOffset ?? 0,
        blur: containerDesign.blur ?? 0,
        brightness: containerDesign.brightness ?? 100,
        contrast: containerDesign.contrast ?? 100,
        backgroundAnimation: containerDesign.backgroundAnimation ?? 'none',
        bioHighlight: containerDesign.bioHighlight ?? 0,
        bioHighlightColor: containerDesign.bioHighlightColor ?? '#ffffff',
        bioStroke: containerDesign.bioStroke ?? 0,
        nameHighlight: containerDesign.nameHighlight ?? 0,
        nameHighlightColor: containerDesign.nameHighlightColor ?? '#ffffff',
        infoDesign: containerDesign.infoDesign ?? null,
      };
    },
    headerClear: () => initialState,

    headerUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    headerUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },
    headerUpdateLayout: (state, action) => {
      const prevLayout = state.layout;
      state.layout = action.payload;

      // Set default profilePic height when switching to layout 4 or 5
      if (action.payload === 4 && prevLayout !== 4) {
        state.profilePic.height = 462;
      } else if (action.payload === 5 && prevLayout !== 5) {
        state.profilePic.height = 250;
      }
    },
    headerUpdateViewType: (state, action) => {
      state.viewType = action.payload;
    },
    headerUpdateButtons: (state, action) => {
      state.buttons = action.payload;
    },
    headerUpdateContacts: (state, action) => {
      state.contacts = action.payload;
    },
    headerUpdateProfilePic: (state, action) => {
      state.profilePic = action.payload;
    },
    headerUpdateCover: (state, action) => {
      state.cover = action.payload;
    },
    headerUpdateFullName: (state, action) => {
      state.fullName = action.payload;
    },
    headerUpdateShortBio: (state, action) => {
      state.shortBio = action.payload;
    },
    headerUpdateShortBioSize: (state, action) => {
      state.shortBioSize = action.payload;
    },
    headerUpdateExternalUrl: (state, action) => {
      state.externalUrl = action.payload;
    },
    headerUpdateLocation: (state, action) => {
      state.location = action.payload;
    },
    headerUpdateLocationMap: (state, action) => {
      state.locationMap = action.payload;
    },
    headerUpdateIsVerifiedBadge: (state, action) => {
      state.isVerifiedBadge = action.payload;
    },
    headerUpdateProfileBadges: (state, action) => {
      state.profileBadges = action.payload;
    },
    headerUpdateIsShowProfilePic: (state, action) => {
      state.profilePic.isDisplay = action.payload;
    },
    headerUpdateSocial: (state, action) => {
      state.social = action.payload;
    },
    headerUpdateThought: (state, action) => {
      state.thought = action.payload;
    },
    headerUpdateThoughtButtonTitle: (state, action) => {
      state.thoughtButtonTitle = action.payload;
    },
    headerUpdateThoughtButtonUrl: (state, action) => {
      state.thoughtButtonUrl = action.payload;
    },
    headerUpdateStories: (state, action) => {
      state.stories = action.payload;
    },
    headerUpdateStoryUrl: (state, action) => {
      state.storyUrl = action.payload;
    },
    headerUpdateMusic: (state, action) => {
      state.music = action.payload;
    },
    headerUpdateSubscribeOptions: (state, action) => {
      state.subscribeOptions = action.payload;
    },
    headerUpdateSliderItems: (state, action) => {
      state.sliderItems = action.payload;
    },
    headerUpdateStatuses: (state, action) => {
      state.statuses = action.payload;
    },
    headerUpdateProfilePicRatio: (state, action) => {
      state.profilePicRatio = action.payload;
    },
    headerUpdateCutoutUrl: (state, action) => {
      state.profilePic.cutoutUrl = action.payload;
    },
    headerUpdateCutoutVerticalUrl: (state, action) => {
      state.profilePic.cutoutVerticalUrl = action.payload;
    },

    headerUpdateVCard: (state, action) => {
      state.vCard = action.payload;
    },
    headerUpdateIsVCard: (state, action) => {
      state.isVCard = action.payload;
    },

    headerUpdatePictureDesign: (state, action) => {
      state.pictureDesign = action.payload;
    },
    headerUpdateButtonDesign: (state, action) => {
      state.buttonDesign = action.payload;
    },
    headerUpdateContainerDesign: (state, action) => {
      state.containerDesign = action.payload;
    },
    headerUpdateShowTotalFollowers: (state, action) => {
      state.showTotalFollowers = action.payload;
    },
    headerUpdateTotalFollowersColor: (state, action) => {
      state.totalFollowersColor = action.payload;
    },
    headerUpdateTotalFollowers: (state, action) => {
      state.totalFollowers = action.payload;
    },
    headerUpdateTags: (state, action) => {
      state.tags = action.payload;
    },
    headerUpdateTagsIsActive: (state, action) => {
      state.tags.isActive = action.payload;
    },
    headerUpdateTagsItems: (state, action) => {
      state.tags.items = action.payload;
    },
    headerUpdateRibbon(state, { payload }) {
      state.ribbon = payload;
    },
    headerUpdateWidget(state, { payload }) {
      state.widget = payload;
    },
    headerUpdateId: (state, action) => {
      state.id = action.payload;
    },
  },
});

export const {
  headerStore,
  headerClear,
  headerUpdateIsLoading,
  headerUpdateIsActive,
  headerUpdateLayout,
  headerUpdateViewType,
  headerUpdateFullName,
  headerUpdateButtons,
  headerUpdateContacts,
  headerUpdateProfilePic,
  headerUpdateCover,
  headerUpdateShortBio,
  headerUpdateShortBioSize,
  headerUpdateExternalUrl,
  headerUpdateLocation,
  headerUpdateLocationMap,
  headerUpdateIsVerifiedBadge,
  headerUpdateProfileBadges,
  headerUpdateIsShowProfilePic,
  headerUpdateSocial,
  headerUpdateThought,
  headerUpdateThoughtButtonTitle,
  headerUpdateThoughtButtonUrl,
  headerUpdateStories,
  headerUpdateStoryUrl,
  headerUpdateMusic,
  headerUpdateButtonDesign,
  headerUpdatePictureDesign,
  headerUpdateContainerDesign,
  headerUpdateSubscribeOptions,
  headerUpdateSliderItems,
  headerUpdateStatuses,
  headerUpdateVCard,
  headerUpdateIsVCard,
  headerUpdateId,
  headerUpdateProfilePicRatio,
  headerUpdateCutoutUrl,
  headerUpdateCutoutVerticalUrl,
  headerUpdateShowTotalFollowers,
  headerUpdateTotalFollowers,
  headerUpdateTotalFollowersColor,
  headerUpdateTags,
  headerUpdateTagsIsActive,
  headerUpdateTagsItems,
  headerUpdateRibbon,
  headerUpdateWidget,
} = headerSlice.actions;

export const selectHeaderIsLoading = (state) => state.header.isLoading;

export const selectHeaderIsActive = (state) => state.header.isActive;
export const selectHeaderId = (state) => state.header.id;
export const selectHeaderIsAdded = (state) => Boolean(state.header.id);
export const selectHeaderUID = (state) => state.header.uid;
export const selectHeaderSID = (state) => state.header.sid;

export const selectHeaderLayout = (state) => state.header.layout;
export const selectHeaderViewType = (state) => state.header.viewType;
export const selectHeaderProfilePic = (state) => state.header.profilePic;
export const selectHeaderCover = (state) => state.header.cover;
export const selectHeaderFullName = (state) => state.header.fullName;
export const selectHeaderShortBio = (state) => state.header.shortBio;
export const selectHeaderShortBioSize = (state) => state.header.shortBioSize;
export const selectHeaderExternalUrl = (state) => state.header.externalUrl;
export const selectHeaderLocation = (state) => state.header.location;
export const selectHeaderLocationMap = (state) => state.header.locationMap;
export const selectHeaderIsVerifiedBadge = (state) => state.header.isVerifiedBadge;
export const selectHeaderProfileBadges = (state) => state.header.profileBadges;
export const selectHeaderShowProfilePic = (state) => state.header.profilePic.isDisplay;
export const selectHeaderSocial = (state) => state.header.social;
export const selectHeaderButtons = (state) => state.header.buttons;
export const selectHeaderThought = (state) => state.header.thought;
export const selectHeaderThoughtButtonTitle = (state) => state.header.thoughtButtonTitle;
export const selectHeaderThoughtButtonUrl = (state) => state.header.thoughtButtonUrl;
export const selectHeaderStories = (state) => state.header.stories;
export const selectHeaderStoryUrl = (state) => state.header.storyUrl;
export const selectHeaderSubscribeOptions = (state) => state.header.subscribeOptions;
export const selectHeaderVCard = (state) => state.header.vCard;
export const selectHeaderIsVCard = (state) => state.header.isVCard;
export const selectHeaderPictureDesign = (state) => state.header.pictureDesign;
export const selectHeaderButtonDesign = (state) => state.header.buttonDesign;
export const selectHeaderContainerDesign = (state) => state.header.containerDesign;
export const selectHeaderContacts = (state) => state.header.contacts;
export const selectHeaderSection = (state) => state.header;
export const selectHeaderSliderItems = (state) => state.header.sliderItems;
export const selectHeaderMusic = (state) => state.header.music;
export const selectHeaderStatuses = (state) => state.header.statuses;
export const selectHeaderProfilePicRatio = (state) => state.header.profilePicRatio;
export const selectHeaderCutoutUrl = (state) => state.header.profilePic.cutoutUrl;
export const selectHeaderCutoutVerticalUrl = (state) => state.header.profilePic.cutoutVerticalUrl;
export const selectHeaderShowTotalFollowers = (state) => state.header.showTotalFollowers;
export const selectHeaderTotalFollowersColor = (state) => state.header.totalFollowersColor;
export const selectHeaderTotalFollowers = (state) => state.header.totalFollowers;
export const selectHeaderTags = (state) => state.header.tags;
export const selectHeaderTagsIsActive = (state) => state.header.tags?.isActive ?? true;
export const selectHeaderTagsItems = (state) => state.header.tags?.items ?? [];
export const selectHeaderRibbon = (state) => state.header.ribbon;
export const selectHeaderWidget = (state) => state.header.widget;

export default headerSlice.reducer;
